import React, { useState } from "react";
import { motion } from "framer-motion";
import { APP_ICONS } from "../../../../apps-integration/assets/icons";
import { colors } from "../../../../ui/colors";

interface BillingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onContinue?: (quantity: number, paymentMethod: PaymentMethod) => void;
}

type PaymentMethod = "stripe" | "paypal";

export function BillingModal({ isOpen, onClose, onContinue }: BillingModalProps) {
    const [quantity, setQuantity] = useState(1);
    const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>("stripe");

    if (!isOpen) return null;

    const pricePerSite = 12.0;
    const totalPrice = pricePerSite * quantity;

    const handleContinue = () => {
        if (onContinue) {
            onContinue(quantity, selectedPayment);
        }
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <motion.div
                style={modalStyle}
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.25 }}
            >
                <button onClick={onClose} style={closeButtonStyle} aria-label="Close modal">
                    ✕
                </button>

                <div style={contentStyle}>
                    <h2 style={titleStyle}>Upgrade to publish more sites</h2>
                    <p style={descriptionStyle}>
                        You've reached your 1-site limit on the free plan. Add capacity to your
                        account to launch more projects instantly.
                    </p>

                    {/* Pricing Card */}
                    <div style={pricingCardStyle}>
                        <div style={pricingHeaderStyle}>
                            <div style={pricingTitleContainerStyle}>
                                <h3 style={pricingTitleStyle}>Additional Site Capacity</h3>
                            </div>
                            <div style={priceContainerStyle}>
                                <div style={priceLabelStyle}>Price per site</div>
                                <div style={priceStyle}>
                                    ${pricePerSite.toFixed(2)}
                                    <span style={priceUnitStyle}>/mo</span>
                                </div>
                            </div>
                        </div>

                        <div style={featuresListStyle}>
                            <div style={featureItemStyle}>
                                <span style={checkIconStyle}>✓</span>
                                <span style={featureTextStyle}>Premium Support (24/7)</span>
                            </div>
                            <div style={featureItemStyle}>
                                <span style={checkIconStyle}>✓</span>
                                <span style={featureTextStyle}>Custom Domain Mapping</span>
                            </div>
                            <div style={featureItemStyle}>
                                <span style={checkIconStyle}>✓</span>
                                <span style={featureTextStyle}>SSL Certificate Included</span>
                            </div>
                        </div>

                        <div style={quantitySectionStyle}>
                            <div style={quantityControlsStyle}>
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    style={quantityButtonStyle}
                                    disabled={quantity <= 1}
                                >
                                    −
                                </button>
                                <span style={quantityValueStyle}>{quantity}</span>
                                <button
                                    onClick={() => setQuantity(quantity + 1)}
                                    style={{
                                        ...quantityButtonStyle,
                                        ...quantityButtonPlusStyle,
                                    }}
                                >
                                    +
                                </button>
                            </div>
                            <div style={totalPriceContainerStyle}>
                                <div style={totalLabelStyle}>TOTAL PRICE</div>
                                <div style={totalPriceStyle}>
                                    ${totalPrice.toFixed(2)}
                                    <span style={priceUnitStyle}>/mo</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div style={sectionStyle}>
                        <h3 style={sectionTitleStyle}>SELECT PAYMENT METHOD</h3>
                        <div style={paymentMethodsStyle}>
                            {/* Stripe Card */}
                            <div
                                style={{
                                    ...paymentCardStyle,
                                    ...(selectedPayment === "stripe" ? selectedPaymentCardStyle : {}),
                                }}
                                onClick={() => setSelectedPayment("stripe")}
                            >
                                <div style={paymentLogoContainerStyle}>
                                    <img
                                        src={APP_ICONS.stripe}
                                        alt="Stripe"
                                        style={{ height: 40, width: "auto" }}
                                    />
                                </div>
                                <h4 style={paymentTitleStyle}>Stripe</h4>
                                <p style={paymentDescStyle}>Securely via Stripe</p>
                                {selectedPayment === "stripe" && (
                                    <div style={selectedBadgeStyle}>Selected</div>
                                )}
                                {selectedPayment !== "stripe" && (
                                    <button style={selectButtonStyle}>Select</button>
                                )}
                            </div>

                            {/* PayPal Card */}
                            <div
                                style={{
                                    ...paymentCardStyle,
                                    ...(selectedPayment === "paypal" ? selectedPaymentCardStyle : {}),
                                }}
                                onClick={() => setSelectedPayment("paypal")}
                            >
                                <div style={paymentLogoContainerStyle}>
                                    <img
                                        src={APP_ICONS.paypal}
                                        alt="PayPal"
                                        style={{ height: 40, width: "auto" }}
                                    />
                                </div>
                                <h4 style={paymentTitleStyle}>PayPal</h4>
                                <p style={paymentDescStyle}>Faster checkout flow</p>
                                {selectedPayment === "paypal" && (
                                    <div style={selectedBadgeStyle}>Selected</div>
                                )}
                                {selectedPayment !== "paypal" && (
                                    <button style={selectButtonStyle}>Select</button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Payment Button Section */}
                    <div style={paymentButtonSectionStyle}>
                        {selectedPayment === "stripe" && (
                            <>
                                <button
                                    onClick={handleContinue}
                                    style={stripePaymentButtonStyle}
                                >
                                    <div style={paymentButtonContentStyle}>
                                        <span>Continue with Stripe</span>
                                    </div>
                                </button>
                                <div style={securePaymentStyle}>
                                    <span style={secureTextStyle}>SECURE PAYMENT POWERED BY STRIPE</span>
                                </div>
                            </>
                        )}
                        {selectedPayment === "paypal" && (
                            <>
                                <button
                                    onClick={handleContinue}
                                    style={paypalPaymentButtonStyle}
                                >
                                    <div style={paymentButtonContentStyle}>
                                        <span>Continue with PayPal</span>
                                    </div>
                                </button>
                                <div style={securePaymentStyle}>
                                    <span style={secureTextStyle}>SECURE PAYMENT POWERED BY PAYPAL</span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div style={footerStyle}>
                        <button style={helpButtonStyle}>
                            Need help? Contact support
                        </button>
                        <button onClick={onClose} style={secondaryBtn}>
                            Not now
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

// ============ STYLES ============

const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1100,
    padding: 20,
    overflowY: "auto",
};

const modalStyle: React.CSSProperties = {
    position: "relative",
    backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
    borderRadius: 16,
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    maxWidth: 680,
    width: "95%",
    maxHeight: "90vh",
    overflowY: "auto",
};

const closeButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: 16,
    right: 16,
    background: "none",
    border: "none",
    fontSize: 22,
    cursor: "pointer",
    color: colors.gray[300],
    width: 32,
    height: 32,
    zIndex: 10,
};

const contentStyle: React.CSSProperties = {
    padding: "32px 40px 28px",
    display: "flex",
    flexDirection: "column",
    gap: 28,
};

const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    color: colors.gray[500],
};

const descriptionStyle: React.CSSProperties = {
    margin: 0,
    marginTop: -12,
    fontSize: 14,
    lineHeight: 1.6,
    color: colors.gray[300],
};

const pricingCardStyle: React.CSSProperties = {
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    border: "1px solid var(--fx-surface-border, #E2E8F0)",
    borderRadius: 12,
    padding: 24,
};

const pricingHeaderStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
};

const pricingTitleContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
};

const pricingTitleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: colors.gray[500],
};

const priceContainerStyle: React.CSSProperties = {
    textAlign: "right",
};

const priceLabelStyle: React.CSSProperties = {
    fontSize: 11,
    color: colors.gray[300],
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4,
};

const priceStyle: React.CSSProperties = {
    fontSize: 24,
    fontWeight: 700,
    color: colors.gray[500],
};

const priceUnitStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 400,
    color: colors.gray[300],
};

const featuresListStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 20,
};

const featureItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
};

const checkIconStyle: React.CSSProperties = {
    width: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: colors.orange.base,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
};

const featureTextStyle: React.CSSProperties = {
    fontSize: 14,
    color: colors.gray[400],
};

const quantitySectionStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 20,
    borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
};

const quantityControlsStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 16,
};

const quantityButtonStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "2px solid var(--fx-surface-border, #E2E8F0)",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    fontSize: 20,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.gray[400],
    transition: "all 0.2s",
};

const quantityButtonPlusStyle: React.CSSProperties = {
    backgroundColor: colors.orange.base,
    borderColor: colors.orange.base,
    color: "#fff",
};

const quantityValueStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 600,
    color: colors.gray[500],
    minWidth: 30,
    textAlign: "center",
};

const totalPriceContainerStyle: React.CSSProperties = {
    textAlign: "right",
};

const totalLabelStyle: React.CSSProperties = {
    fontSize: 10,
    color: colors.gray[300],
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4,
};

const totalPriceStyle: React.CSSProperties = {
    fontSize: 28,
    fontWeight: 700,
    color: colors.orange.base,
};

const sectionStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 16,
};

const sectionTitleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 12,
    fontWeight: 700,
    color: colors.gray[500],
    textTransform: "uppercase",
    letterSpacing: "0.05em",
};

const paymentMethodsStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 16,
};

const paymentCardStyle: React.CSSProperties = {
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    border: "2px solid var(--fx-surface-border, #E2E8F0)",
    borderRadius: 12,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    transition: "all 0.2s",
    position: "relative",
};

const selectedPaymentCardStyle: React.CSSProperties = {
    borderColor: colors.orange.base,
    backgroundColor: "var(--fx-colors-warning-50, #FFF7ED)",
};

const paymentLogoContainerStyle: React.CSSProperties = {
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
};

const paymentTitleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: colors.gray[500],
};

const paymentDescStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 12,
    color: colors.gray[300],
    textAlign: "center",
};

const selectedBadgeStyle: React.CSSProperties = {
    marginTop: 8,
    padding: "6px 16px",
    borderRadius: 20,
    backgroundColor: colors.orange.base,
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
};

const selectButtonStyle: React.CSSProperties = {
    marginTop: 8,
    padding: "6px 16px",
    borderRadius: 20,
    border: "1px solid var(--fx-surface-border, #E2E8F0)",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    color: colors.gray[400],
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
};

const paymentButtonSectionStyle: React.CSSProperties = {
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    border: "1px solid var(--fx-surface-border, #E2E8F0)",
    borderRadius: 12,
    padding: 40,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 20,
};

const paymentButtonContentStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "center",
};

const stripePaymentButtonStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: 450,
    padding: "18px 32px",
    borderRadius: 10,
    border: "none",
    fontSize: 16,
    fontWeight: 600,
    background: "#635BFF",
    color: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 4px 12px rgba(99, 91, 255, 0.3)",
};

const paypalPaymentButtonStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: 450,
    padding: "18px 32px",
    borderRadius: 10,
    border: "none",
    fontSize: 16,
    fontWeight: 600,
    background: "#0070BA",
    color: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 4px 12px rgba(0, 112, 186, 0.3)",
};

const securePaymentStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
};

const secureTextStyle: React.CSSProperties = {
    fontSize: 11,
    color: colors.gray[300],
    fontWeight: 600,
    letterSpacing: "0.05em",
};

const footerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 8,
};

const helpButtonStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    color: colors.gray[400],
    fontSize: 13,
    cursor: "pointer",
    padding: "8px 0",
};

const secondaryBtn: React.CSSProperties = {
    padding: "12px 24px",
    borderRadius: 10,
    border: `1px solid var(--fx-surface-borderstrong, #CBD5E1)`,
    fontSize: 14,
    fontWeight: 600,
    background: "var(--fx-surface-base, #FFFFFF)",
    color: colors.gray[400],
    cursor: "pointer",
    transition: "all 0.2s",
};